/**
 * Chat state for the standalone widget. Tokens arrive live over the Brain
 * Session's follow; POST endpoints return immediately.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { QuickAction } from '../types';
import type { ChatMessage, CarouselData, ChartData, DisplayFormData, ToolCallData, WidgetParamField } from './types';
import { readCookieParams, readCookieShim } from './cookieParams';
import { useT } from '../i18n';
/** Common interface for the visitor's Brain client and the in-platform preview client */
interface WidgetApiClient extends FollowingClient {
  getApiKey(): string | null;
  /**
   * localStorage key for the conversation id; preview scopes it by agent since
   * it has no API key. `null` remembers no conversation.
   */
  getStorageKey?(): string | null;
  createConversation(): Promise<string>;
  sendMessage(conversationId: string, content: string): Promise<{ status: string; conversation_id: string }>;
  /** Submit a form with its trigger `context` and the declared `params` bag. */
  submitForm(conversationId: string, formId: string, values: Record<string, unknown>, context?: Record<string, unknown>, params?: Record<string, unknown>): Promise<{ status: string; conversation_id: string }>;
  /** The form a trigger names, authorized by the conversation. */
  fetchForm(formId: string, conversationId?: string | null): Promise<DisplayFormData>;
  getMessageHistory?(conversationId: string): Promise<Array<{ role: string; content: string; failed?: boolean; carousels?: Record<string, unknown>[] | null; charts?: Record<string, unknown>[] | null; quick_actions?: Array<{ label: string; search_query?: string | null; form_id?: string | null; bg_color?: string | null; text_color?: string | null }> | null; submitted_form_id?: string }>>;
  getStreamStatus?(conversationId: string): Promise<{
    active: boolean;
    status?: string;
    chunk_count?: number;
    /** The turn being written, so live fragments the replay showed are skipped. */
    turn_event_id?: string | null;
  }>;
  getStreamChunks?(conversationId: string, fromIndex?: number): Promise<{ chunks: Array<{ event: string; data: Record<string, unknown> }>; total: number; status: string }>;
}
import { processStreamEvent, parseCarouselEvent } from './eventParsers';
import type { StreamCallbacks } from './eventParsers';
import { emptyTurn, processBrainSessionEvent } from './brainEventParsers';
import type { BrainSessionFrame } from './brainEventParsers';
import { useSessionFollow } from './useSessionFollow';
import type { FollowingClient, WSMessage } from './useSessionFollow';

/** What a Brain turn draws into the Agent's bubble, as frame names. */
const AGENT_OUTPUTS = new Set(['token', 'stream', 'message', 'display_carousel', 'display_form', 'display_chart', 'turn_failed']);

/** The server's refusal of a message sent while the previous reply is still written. */
const TURN_IN_PROGRESS = 'BRAIN_SESSION_TURN_IN_PROGRESS';

/** Whether a failed post was refused because the Agent is still answering. */
function isStillAnswering(err: unknown): boolean {
  return err instanceof Error && 'code' in err && err.code === TURN_IN_PROGRESS;
}

interface UseWidgetChatOptions {
  apiClient: WidgetApiClient;
  /** Declared param fields; cookie-source ones are read fresh and sent under context.cookies on form submit */
  paramFields?: WidgetParamField[];
}

/** How the active form was opened, so widgets never infer it from a null message id. */
export type ActiveFormSource =
  | { kind: 'inline'; messageId: string }
  | { kind: 'trigger' }
  | { kind: 'topButton' };

interface UseWidgetChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  /** Currently active form (from DISPLAY_FORM tool) */
  activeForm: DisplayFormData | null;
  /** How the active form was opened (null when no form is open) */
  activeFormSource: ActiveFormSource | null;
  /** Set of submitted form IDs (topButton / persistent-form once-per-form tracking) */
  submittedFormIds: Set<string>;
  /** Whether a stored conversation is still restoring; its sent forms are unknown until then. */
  isRestoring: boolean;
  /** Open an inline agent form in the drawer, keyed by its message id */
  openForm: (form: DisplayFormData, messageId: string) => void;
  closeForm: () => void;
  submitForm: (formId: string, values: Record<string, unknown>) => Promise<void>;
  /** Trigger a form display by fetching form data and showing it */
  triggerForm: (
    formId: string,
    context?: Record<string, unknown>,
    source?: 'trigger' | 'topButton',
  ) => Promise<void>;
  /** Context data from carousel item when form was triggered via button */
  activeFormContext: Record<string, unknown> | null;
  /** Current tool call being executed (for thinking indicator) */
  currentToolCall: ToolCallData | null;
  /** Ordered tool calls observed during the in-flight stream. Last item is active. */
  toolCallHistory: ToolCallData[];
  quickActions: QuickAction[];
  isQuickActionsLoading: boolean;
}

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function deriveStorageKey(apiKey: string | null): string {
  const prefix = apiKey ? apiKey.slice(0, 12) : 'preview';
  return `kapia_webchat_${prefix}`;
}

export function useWidgetChat({ apiClient, paramFields }: UseWidgetChatOptions): UseWidgetChatReturn {
  const t = useT();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeForm, setActiveForm] = useState<DisplayFormData | null>(null);
  const [activeFormSource, setActiveFormSource] = useState<ActiveFormSource | null>(null);
  const [activeFormContext, setActiveFormContext] = useState<Record<string, unknown> | null>(null);
  const [submittedFormIds, setSubmittedFormIds] = useState<Set<string>>(new Set());
  const [currentToolCall, setCurrentToolCall] = useState<ToolCallData | null>(null);
  const [toolCallHistory, setToolCallHistory] = useState<ToolCallData[]>([]);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [isQuickActionsLoading, setIsQuickActionsLoading] = useState(false);

  const storageKey = apiClient.getStorageKey ? apiClient.getStorageKey() : deriveStorageKey(apiClient.getApiKey());
  const [conversationId, setConversationIdRaw] = useState<string | null>(() => {
    if (storageKey === null) return null;
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const setConversationId = useCallback((id: string | null) => {
    setConversationIdRaw(id);
    if (storageKey === null) return;
    try {
      if (id) {
        localStorage.setItem(storageKey, id);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch { /* localStorage unavailable */ }
  }, [storageKey]);
  const currentAssistantMessageRef = useRef<string>('');
  const assistantMessageIdRef = useRef<string | null>(null);
  // What the Brain transport's current turn has settled. Lives across the
  // frames of one turn and is cleared by the frame that ends it.
  const brainTurnRef = useRef(emptyTurn());
  // Brain frames that arrive while a reload restores the reply being written,
  // replayed once the replay is seeded: drawn before it, a fragment would be
  // wiped by the restore and a `turn_complete` undone by it.
  const heldBrainFramesRef = useRef<BrainSessionFrame[] | null>(null);
  // Whether a turn is in flight, read by the frame handler: a Brain turn's
  // suggestions arrive after its `turn_complete`, and a set for a reply the
  // visitor has already answered must not land under the next one.
  const turnInFlightRef = useRef(false);
  // Restore only when mounted with a stored ID; otherwise createConversation's
  // null→new-id flip would re-fire the restore and wipe the new ID.
  const restoredRef = useRef(conversationId === null);
  const [isRestoring, setIsRestoring] = useState(conversationId !== null);

  const pendingConversationRef = useRef<Promise<string> | null>(null);
  const { subscribe, addMessageHandler, removeMessageHandler } = useSessionFollow(apiClient);

  const getCallbacks = useCallback((): StreamCallbacks => ({
    onToken: (tokenContent: string) => {
      setCurrentToolCall(null);
      currentAssistantMessageRef.current += tokenContent;
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      const content = currentAssistantMessageRef.current;
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, content } : msg)),
      );
    },
    // Duplication guard fired: replace (don't append) the in-progress text
    // with the canonical single copy.
    onTextReplace: (content: string) => {
      currentAssistantMessageRef.current = content;
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      // The canonical text is what anything arriving after it extends, so the
      // accumulator moves with it. Without this a token following a replace
      // would append to the superseded stream and undo the correction.
      currentAssistantMessageRef.current = content;
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, content } : msg)),
      );
    },
    onTurnFailed: () => {
      const content = t('chat.errorReply');
      currentAssistantMessageRef.current = content;
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, content } : msg)),
      );
    },
    onComplete: () => {
      turnInFlightRef.current = false;
      setCurrentToolCall(null);
      setToolCallHistory((prev) =>
        prev.map((tc) => (tc.status === 'calling' ? { ...tc, status: 'completed' } : tc)),
      );
      setIsStreaming(false);
      setIsQuickActionsLoading(false);
      const msgId = assistantMessageIdRef.current;
      if (msgId) {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === msgId ? { ...msg, isCarouselBuilding: false } : msg)),
        );
      }
    },
    onError: (err: Error) => {
      turnInFlightRef.current = false;
      setCurrentToolCall(null);
      setToolCallHistory((prev) =>
        prev.map((tc) => (tc.status === 'calling' ? { ...tc, status: 'completed' } : tc)),
      );
      setError(err.message);
      setIsStreaming(false);
      setIsQuickActionsLoading(false);
      const msgId = assistantMessageIdRef.current;
      if (msgId) {
        setMessages((prev) => prev.filter((msg) => msg.id !== msgId));
      }
    },
    onDisplayCarousel: (carouselData: CarouselData) => {
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === msgId ? { ...msg, displayCarousel: carouselData, isCarouselBuilding: false } : msg,
        ),
      );
    },
    onDisplayForm: (formData: DisplayFormData) => {
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      setQuickActions([]);
      setIsQuickActionsLoading(false);
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, displayForm: formData } : msg)),
      );
    },
    onDisplayChart: (chartData: ChartData) => {
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, displayChart: chartData } : msg)),
      );
    },
    onToolCall: (toolCall: ToolCallData) => {
      if (toolCall.status === 'calling') {
        setCurrentToolCall(toolCall);
        setToolCallHistory((prev) => {
          const last = prev[prev.length - 1];
          // Same tool is re-emitting while still in flight: update the trailing
          // entry in place so progress messages don't stack up as duplicates.
          if (
            last &&
            last.status === 'calling' &&
            last.toolName === toolCall.toolName
          ) {
            return [...prev.slice(0, -1), toolCall];
          }
          // A different tool has started: close any prior in-flight entry,
          // then append the new active one.
          const closed = prev.map((tc) =>
            tc.status === 'calling' ? { ...tc, status: 'completed' } : tc,
          );
          return [...closed, toolCall];
        });
      } else {
        setCurrentToolCall(null);
        setToolCallHistory((prev) =>
          prev.map((tc) =>
            tc.status === 'calling' && tc.toolName === toolCall.toolName
              ? { ...tc, status: toolCall.status, executionMessage: toolCall.executionMessage ?? tc.executionMessage }
              : tc,
          ),
        );
      }
    },
    onCarouselBuilding: () => {
      const msgId = assistantMessageIdRef.current;
      if (!msgId) return;
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, isCarouselBuilding: true } : msg)),
      );
    },
    onQuickActionsLoading: () => {
      setIsQuickActionsLoading(true);
    },
    onQuickActions: (actions: QuickAction[]) => {
      setQuickActions(actions);
      setIsQuickActionsLoading(false);
    },
  }), [t]);

  useEffect(() => {
    const callbacksRef = getCallbacks();

    const handler = (msg: WSMessage) => {
      if (msg.type !== 'message') return;

      // The Brain publishes an output row, dispatched by its own parser.
      if (msg.channel === 'brain_session') {
        // Whose Session this is. Frames of a conversation the visitor cleared
        // can still be in flight when the follow moves to the one replacing
        // it, and would otherwise be drawn into it.
        if (!msg.data || msg.id !== conversationId) return;
        const frame = msg.data as unknown as BrainSessionFrame;
        if (heldBrainFramesRef.current !== null) {
          heldBrainFramesRef.current.push(frame);
          return;
        }
        if (frame.event === 'quick_actions' && turnInFlightRef.current) return;
        processBrainSessionEvent(
          frame,
          brainTurnRef.current,
          callbacksRef,
        );
      }
    };

    addMessageHandler('streamEvents', handler);
    return () => removeMessageHandler('streamEvents');
  }, [addMessageHandler, removeMessageHandler, getCallbacks, conversationId]);

  // Safety timeout: clear quick-actions loading state if it lingers after streaming ends
  useEffect(() => {
    if (isQuickActionsLoading && !isStreaming) {
      const timer = setTimeout(() => {
        setIsQuickActionsLoading(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isQuickActionsLoading, isStreaming]);

  useEffect(() => {
    if (restoredRef.current || !conversationId) return;
    restoredRef.current = true;

    const restore = async () => {
      // No history endpoint (e.g. preview mode): treat any stored ID as stale —
      // there's no way to verify it still has messages, so start fresh.
      if (!apiClient.getMessageHistory) {
        setConversationId(null);
        setIsRestoring(false);
        return;
      }
      try {
        const history = await apiClient.getMessageHistory(conversationId);
        if (history.length === 0) {
          // Stored conversation has no messages — discard so a fresh one is
          // created on the next send instead of reusing the empty stale ID.
          setConversationId(null);
          return;
        }

        // A sent form is restored as sent rather than drawn, so a reload
        // cannot re-enable it and let the visitor send it twice.
        const sent = history.flatMap((msg) => (msg.submitted_form_id ? [msg.submitted_form_id] : []));
        if (sent.length > 0) setSubmittedFormIds(new Set(sent));
        setIsRestoring(false);
        const drawn = history.filter((msg) => !msg.submitted_form_id);

        const restored: ChatMessage[] = drawn.map((msg) => ({
          id: generateId(),
          role: msg.role as 'user' | 'assistant',
          content: msg.failed ? t('chat.errorReply') : msg.content,
          timestamp: new Date(),
          displayCarousel: msg.carousels?.[0]
            ? (parseCarouselEvent(msg.carousels[0] as unknown as Record<string, unknown>))
            : undefined,
        }));
        setMessages(restored);

        // Restore quick actions only when the reply carrying them is still the
        // last thing said: once the visitor has written again they answer a
        // reply the conversation has moved past, and would be drawn under the
        // visitor's own message.
        const last = drawn[drawn.length - 1];
        if (last?.role === 'assistant' && last.quick_actions && last.quick_actions.length > 0) {
          setQuickActions(last.quick_actions);
        }

        // Follow this conversation, holding its frames until the reply being
        // written (if any) is restored.
        heldBrainFramesRef.current = [];
        subscribe(conversationId);

        // Check for active stream and resume if one exists. A failed read is
        // "nothing streaming": the conversation itself was already restored.
        const status = apiClient.getStreamStatus
          ? await apiClient.getStreamStatus(conversationId).catch(() => ({ active: false, turn_event_id: null }))
          : null;
        if (status) {
          if (status.active && apiClient.getStreamChunks) {
            setIsStreaming(true);
            const aId = generateId();
            currentAssistantMessageRef.current = '';
            assistantMessageIdRef.current = aId;
            setMessages((prev) => [...prev, { id: aId, role: 'assistant', content: '', timestamp: new Date() }]);

            const { chunks, total } = await apiClient.getStreamChunks(conversationId);
            const callbacks = getCallbacks();
            for (const chunk of chunks) {
              processStreamEvent(chunk.event, chunk.data, callbacks);
            }
            // The follow delivers the remaining live chunks. Each carries its
            // index, and one below `total` is a fragment the replay above
            // already drew.
            brainTurnRef.current = {
              ...emptyTurn(),
              turnEventId: status.turn_event_id ?? null,
              nextToken: total,
            };
            turnInFlightRef.current = true;
          }
        }
      } catch {
        // Conversation expired or invalid — clear and start fresh
        setConversationId(null);
      } finally {
        setIsRestoring(false);
        const held = heldBrainFramesRef.current ?? [];
        heldBrainFramesRef.current = null;
        // A reply that settled while the conversation was being restored: its
        // rows are held here, but no turn was writing when the buffer was read,
        // so nothing opened a bubble for them to draw into.
        if (!assistantMessageIdRef.current && held.some((frame) => AGENT_OUTPUTS.has(frame.event))) {
          const aId = generateId();
          currentAssistantMessageRef.current = '';
          assistantMessageIdRef.current = aId;
          setMessages((prev) => [...prev, { id: aId, role: 'assistant', content: '', timestamp: new Date() }]);
        }
        const callbacks = getCallbacks();
        for (const frame of held) {
          if (frame.event === 'quick_actions' && turnInFlightRef.current) continue;
          processBrainSessionEvent(frame, brainTurnRef.current, callbacks);
        }
      }
    };

    restore();
  }, [conversationId, apiClient, subscribe, getCallbacks, storageKey, setConversationId, t]);

  /** Create assistant placeholder for streaming */
  const startAssistantPlaceholder = useCallback(() => {
    const id = generateId();
    currentAssistantMessageRef.current = '';
    // Cleared here as well as on `turn_complete`, since a turn whose completion
    // never arrived would otherwise prepend its text to the next reply.
    brainTurnRef.current = emptyTurn();
    turnInFlightRef.current = true;
    assistantMessageIdRef.current = id;
    setToolCallHistory([]);
    setMessages((prev) => [
      ...prev,
      { id, role: 'assistant', content: '', timestamp: new Date() },
    ]);
    return id;
  }, []);

  /** Send a message and stream the reply. */
  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming || !content.trim()) return;

      setError(null);
      setIsStreaming(true);
      setQuickActions([]);
      setIsQuickActionsLoading(false);
      setActiveForm(null);
      setActiveFormSource(null);
      setActiveFormContext(null);

      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Held so a refusal can point the stream back at the reply still being
      // written, which is what the server refused this message over.
      const previousAssistantId = assistantMessageIdRef.current;
      const previousTurn = brainTurnRef.current;
      const previousInFlight = turnInFlightRef.current;

      startAssistantPlaceholder();

      try {
        let currentConversationId = conversationId;
        if (!currentConversationId) {
          currentConversationId = await apiClient.createConversation();
          setConversationId(currentConversationId);
        }

        // Followed before the POST so no early chunk is missed.
        subscribe(currentConversationId);

        await apiClient.sendMessage(currentConversationId, content.trim());
      } catch (err) {
        // The previous reply is still being written: the input is closed while it
        // streams, so this is the server holding the line rather than a fault.
        const stillAnswering = isStillAnswering(err);
        if (stillAnswering) {
          const placeholderId = assistantMessageIdRef.current;
          setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id && msg.id !== placeholderId));
          assistantMessageIdRef.current = previousAssistantId;
          brainTurnRef.current = previousTurn;
          turnInFlightRef.current = previousInFlight;
        }
        const errorMessage = stillAnswering
          ? t('chat.stillAnswering')
          : err instanceof Error
            ? err.message
            : t('chat.errorSend');
        setError(errorMessage);
        setIsStreaming(false);
        // After a refusal the ref already points back at the reply still being
        // written; its bubble is not this send's to remove.
        const msgId = isStillAnswering(err) ? null : assistantMessageIdRef.current;
        if (msgId) {
          setMessages((prev) => prev.filter((msg) => msg.id !== msgId || msg.content !== ''));
        }
      }
    },
    [apiClient, isStreaming, conversationId, startAssistantPlaceholder, subscribe, setConversationId, t],
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    setActiveForm(null);
    setActiveFormSource(null);
    setActiveFormContext(null);
    setSubmittedFormIds(new Set());
    setCurrentToolCall(null);
    setToolCallHistory([]);
    setQuickActions([]);
    setIsQuickActionsLoading(false);
    setConversationId(null);
    currentAssistantMessageRef.current = '';
    brainTurnRef.current = emptyTurn();
    assistantMessageIdRef.current = null;
    // Stay "restored" — there's nothing to restore now, and the next
    // createConversation within this session must not retrigger the
    // restore effect (which would race with the user-message save).
    restoredRef.current = true;
  }, [setConversationId]);

  const openForm = useCallback((form: DisplayFormData, messageId: string) => {
    setActiveForm(form);
    setActiveFormSource({ kind: 'inline', messageId });
    // Inline forms carry no trigger context (only triggerForm sets a carousel
    // item); clear any stale context so an inline submit doesn't inherit it.
    setActiveFormContext(null);
  }, []);

  const closeForm = useCallback(() => {
    setActiveForm(null);
    setActiveFormSource(null);
    setActiveFormContext(null);
  }, []);

  const submitForm = useCallback(
    async (formId: string, values: Record<string, unknown>) => {
      if (isStreaming) return;

      setError(null);
      setIsStreaming(true);
      setQuickActions([]);
      setIsQuickActionsLoading(false);

      // Read fresh, to capture a cookie set after init (a mid-chat login).
      const cookies = readCookieShim(paramFields ?? []);
      const hasCookies = Object.keys(cookies).length > 0;
      const formContext =
        activeFormContext || hasCookies
          ? { ...(activeFormContext ?? {}), ...(hasCookies ? { cookies } : {}) }
          : undefined;
      const formData = activeForm;
      const submittedMessageId =
        activeFormSource?.kind === 'inline' ? activeFormSource.messageId : null;

      // A repeatable form must never enter submittedFormIds, which disables every
      // copy and hides a matching top button; other forms submit once.
      const repeatable = !!formData?.allow_multiple_submissions;
      if (repeatable) {
        if (submittedMessageId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === submittedMessageId ? { ...m, submitted: true } : m)),
          );
        }
      } else {
        setSubmittedFormIds((prev) => new Set([...prev, formId]));
      }
      setActiveForm(null);
      setActiveFormSource(null);
      setActiveFormContext(null);

      const echoId = formData ? generateId() : null;
      if (formData && echoId) {
        const userMessage: ChatMessage = {
          id: echoId,
          role: 'user',
          content: '',
          timestamp: new Date(),
          formSubmission: { title: formData.title },
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      const previousAssistantId = assistantMessageIdRef.current;
      const previousTurn = brainTurnRef.current;
      const previousInFlight = turnInFlightRef.current;
      const openedFrom = activeFormSource;
      const openedWith = activeFormContext;

      startAssistantPlaceholder();

      try {
        let currentConversationId = conversationId;
        if (!currentConversationId) {
          currentConversationId = await apiClient.createConversation();
          setConversationId(currentConversationId);
        }

        // Followed before the POST so no chunk the backend publishes is missed.
        subscribe(currentConversationId);

        await apiClient.submitForm(currentConversationId, formId, values, formContext, readCookieParams(paramFields ?? []));
      } catch (err) {
        if (isStillAnswering(err)) {
          // Nothing was submitted: put the form back exactly as it was, so the
          // person can send it once the reply is done.
          const placeholderId = assistantMessageIdRef.current;
          setMessages((prev) =>
            prev
              .filter((msg) => msg.id !== echoId && msg.id !== placeholderId)
              .map((msg) => (msg.id === submittedMessageId ? { ...msg, submitted: false } : msg)),
          );
          if (!repeatable) {
            setSubmittedFormIds((prev) => {
              const next = new Set(prev);
              next.delete(formId);
              return next;
            });
          }
          setActiveForm(formData);
          setActiveFormSource(openedFrom);
          setActiveFormContext(openedWith);
          assistantMessageIdRef.current = previousAssistantId;
          brainTurnRef.current = previousTurn;
          turnInFlightRef.current = previousInFlight;
        }
        const errorMessage = isStillAnswering(err)
          ? t('chat.stillAnswering')
          : err instanceof Error
            ? err.message
            : t('chat.errorSubmitForm');
        setError(errorMessage);
        setIsStreaming(false);
        // After a refusal the ref already points back at the reply still being
        // written; its bubble is not this send's to remove.
        const msgId = isStillAnswering(err) ? null : assistantMessageIdRef.current;
        if (msgId) {
          setMessages((prev) => prev.filter((msg) => msg.id !== msgId || msg.content !== ''));
        }
      }
    },
    [apiClient, isStreaming, conversationId, activeForm, activeFormSource, activeFormContext, paramFields, startAssistantPlaceholder, subscribe, setConversationId, t],
  );

  /**
   * The Session this visitor's conversation runs as, opened if there is none.
   * The in-flight promise is shared, so two quick clicks open one Session.
   */
  const openConversation = useCallback(async () => {
    pendingConversationRef.current ??= apiClient.createConversation();
    try {
      const opened = await pendingConversationRef.current;
      setConversationId(opened);
      subscribe(opened);
      return opened;
    } finally {
      pendingConversationRef.current = null;
    }
  }, [apiClient, setConversationId, subscribe]);

  const triggerForm = useCallback(
    async (
      formId: string,
      context?: Record<string, unknown>,
      source: 'trigger' | 'topButton' = 'trigger',
    ) => {
      if (isStreaming) return;

      setError(null);

      try {
        // A component is drawn inside a Session, and a visitor can open one
        // before saying anything, so the Session is opened here.
        const currentConversationId = conversationId ?? (await openConversation());
        const formData = await apiClient.fetchForm(formId, currentConversationId);
        setActiveForm(formData);
        setActiveFormSource({ kind: source });
        setActiveFormContext(context ?? null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('chat.errorLoadForm');
        setError(errorMessage);
      }
    },
    [apiClient, isStreaming, conversationId, openConversation, t],
  );

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearChat,
    activeForm,
    activeFormSource,
    submittedFormIds,
    isRestoring,
    openForm,
    closeForm,
    submitForm,
    triggerForm,
    activeFormContext,
    currentToolCall,
    toolCallHistory,
    quickActions,
    isQuickActionsLoading,
  };
}
